import type {
  DashboardFilters,
  DeveloperMetrics,
  LeadershipMetrics,
  ScrumMasterMetrics,
  TrendIndicator,
} from '@delivery-metrics/shared';

/**
 * Mock Data Service
 * Provides mock data for development and testing
 */

export class MockDataService {
  /**
   * Generate mock Leadership metrics
   */
  static getLeadershipMetrics(filters: DashboardFilters): LeadershipMetrics {
    const avgHours = 320 + Math.random() * 160; // 320-480 hours (2-3 months)

    return {
      kpis: {
        averageFeatureTimeInvestment: {
          hours: avgHours,
          weeks: avgHours / 40, // 40 hours per week
          months: avgHours / 160, // 160 hours per month
          trend: this.getRandomTrend(),
          previousValue: {
            hours: 350,
            weeks: 8.75,
            months: 2.19,
          },
        },
        featuresDelivered: {
          value: Math.floor(35 + Math.random() * 15),
          unit: 'count',
          trend: this.getRandomTrend(),
          previousValue: 40,
          changePercentage: -5 + Math.random() * 15,
        },
        timeEfficiency: {
          value: 1.2 + Math.random() * 0.8, // Value delivered per FTE hour
          deliveredValue: 50 + Math.random() * 30, // Business value units
          effortInvestment: avgHours * (35 + Math.random() * 15), // Total FTE hours
          valueMethod: 'BusinessValueScore',
          trend: this.getRandomTrend(),
        },
      },
      featureDelivery: {
        byType: {
          'New Feature': Math.floor(15 + Math.random() * 10),
          Enhancement: Math.floor(10 + Math.random() * 8),
          'Bug Fix': Math.floor(5 + Math.random() * 5),
          'Technical Improvement': Math.floor(5 + Math.random() * 5),
        },
        byStatus: {
          Completed: Math.floor(30 + Math.random() * 10),
          'In Progress': Math.floor(8 + Math.random() * 5),
          Planned: Math.floor(12 + Math.random() * 8),
        },
        timeInvestmentBreakdown: [
          {
            type: 'New Feature',
            hours: 4800 + Math.random() * 1600,
            weeks: 120 + Math.random() * 40,
            months: 30 + Math.random() * 10,
          },
          {
            type: 'Enhancement',
            hours: 3200 + Math.random() * 1200,
            weeks: 80 + Math.random() * 30,
            months: 20 + Math.random() * 7.5,
          },
          {
            type: 'Bug Fix',
            hours: 800 + Math.random() * 400,
            weeks: 20 + Math.random() * 10,
            months: 5 + Math.random() * 2.5,
          },
        ],
      },
      effortDistribution: {
        newDevelopment: 55 + Math.random() * 10,
        maintenance: 25 + Math.random() * 10,
        technicalDebt: 15 + Math.random() * 10,
      },
      tShirtSizeDistribution: {
        XS: Math.floor(8 + Math.random() * 5),
        S: Math.floor(20 + Math.random() * 10),
        M: Math.floor(35 + Math.random() * 10),
        L: Math.floor(15 + Math.random() * 10),
        XL: Math.floor(3 + Math.random() * 5),
      },
      featureDeliveryTrends: this.generateTrendData(filters.timeRange),
    };
  }

  /**
   * Generate mock Scrum Master metrics
   */
  static getScrumMasterMetrics(filters: DashboardFilters): ScrumMasterMetrics {
    return {
      sprintPerformance: {
        currentSprint: {
          sprintId: 'sprint-42',
          name: 'Sprint 42',
          progress: 0.65 + Math.random() * 0.3,
          completedWork: Math.floor(38 + Math.random() * 15),
          plannedWork: Math.floor(45 + Math.random() * 15),
          burndownData: [],
        },
        sayDoRatio: {
          value: 0.85 + Math.random() * 0.15,
          unit: 'ratio',
          trend: this.getRandomTrend(),
          previousValue: 0.82,
          changePercentage: 3 + Math.random() * 5,
        },
        velocity: {
          value: Math.floor(40 + Math.random() * 20),
          unit: 'points',
          trend: this.getRandomTrend(),
          previousValue: 45,
          changePercentage: -5 + Math.random() * 15,
        },
        scopeChanges: {
          additions: Math.floor(2 + Math.random() * 5),
          removals: Math.floor(1 + Math.random() * 3),
          modifications: Math.floor(3 + Math.random() * 5),
          impactOnCommitment: 0,
        },
      },
      teamComparison: this.generateTeamComparison(filters.teams),
      velocityTrends: this.generateTrendData(filters.timeRange),
    };
  }

  /**
   * Generate mock Developer metrics
   */
  static getDeveloperMetrics(filters: DashboardFilters): DeveloperMetrics {
    return {
      doraMetrics: {
        deploymentFrequency: {
          value: 2 + Math.random() * 3,
          unit: 'per-day' as const,
          trend: this.getRandomTrend(),
          repositoryBreakdown: this.generateRepositoryBreakdown(
            filters.repositories
          ),
        },
        leadTimeForChanges: {
          value: 12 + Math.random() * 24,
          unit: 'hours' as const,
          trend: this.getRandomTrend(),
          percentiles: {
            p50: 8 + Math.random() * 4,
            p75: 16 + Math.random() * 8,
            p90: 24 + Math.random() * 12,
          },
        },
        changeFailureRate: {
          value: 10 + Math.random() * 15,
          unit: 'percentage' as const,
          trend: this.getRandomTrend(),
          failureBreakdown: [
            { category: 'Build Failures', count: 5, percentage: 40 },
            { category: 'Test Failures', count: 4, percentage: 32 },
            { category: 'Deployment Issues', count: 3, percentage: 24 },
            { category: 'Other', count: 1, percentage: 4 },
          ],
        },
        meanTimeToRecovery: {
          value: 2 + Math.random() * 4,
          unit: 'hours' as const,
          trend: this.getRandomTrend(),
          incidentBreakdown: [
            { severity: 'Critical', count: 2, averageRecoveryTime: 1.5 },
            { severity: 'High', count: 5, averageRecoveryTime: 3 },
            { severity: 'Medium', count: 8, averageRecoveryTime: 4 },
            { severity: 'Low', count: 3, averageRecoveryTime: 6 },
          ],
        },
      },
      repositoryAnalytics: this.generateRepositoryAnalytics(
        filters.repositories
      ),
      pullRequestMetrics: {
        averageSize: {
          value: 250 + Math.random() * 200,
          unit: 'lines',
          trend: this.getRandomTrend(),
          previousValue: 300,
          changePercentage: -10 + Math.random() * 15,
        },
        averageReviewTime: {
          value: 4 + Math.random() * 8,
          unit: 'hours',
          trend: this.getRandomTrend(),
          previousValue: 6,
          changePercentage: -15 + Math.random() * 20,
        },
        mergeRate: {
          value: 0.85 + Math.random() * 0.1,
          unit: 'ratio',
          trend: this.getRandomTrend(),
          previousValue: 0.82,
          changePercentage: 3 + Math.random() * 5,
        },
        rejectionRate: {
          value: 0.05 + Math.random() * 0.05,
          unit: 'ratio',
          trend: this.getRandomTrend(),
          previousValue: 0.08,
          changePercentage: -20 + Math.random() * 15,
        },
      },
      codeReviewMetrics: {
        averageReviewTime: {
          value: 4 + Math.random() * 8,
          unit: 'hours',
          trend: this.getRandomTrend(),
          previousValue: 6,
          changePercentage: -15 + Math.random() * 20,
        },
        reviewParticipation: {
          value: 0.75 + Math.random() * 0.2,
          unit: 'ratio',
          trend: this.getRandomTrend(),
          previousValue: 0.7,
          changePercentage: 5 + Math.random() * 10,
        },
        commentsPerReview: {
          value: 5 + Math.random() * 5,
          unit: 'comments',
          trend: this.getRandomTrend(),
          previousValue: 6,
          changePercentage: -5 + Math.random() * 15,
        },
      },
    };
  }

  /**
   * Generate mock team comparison data
   */
  private static generateTeamComparison(teams?: string[]) {
    const teamNames = teams || ['Alpha', 'Beta', 'Gamma', 'Delta'];
    return teamNames.map(team => ({
      team,
      velocity: Math.floor(30 + Math.random() * 30),
      sayDoRatio: 0.8 + Math.random() * 0.15,
      capacity: Math.floor(300 + Math.random() * 200),
      utilization: 0.7 + Math.random() * 0.25,
    }));
  }

  /**
   * Generate mock repository breakdown
   */
  private static generateRepositoryBreakdown(repositories?: string[]) {
    const repoNames = repositories || this.getRepositories();
    return repoNames.map(repository => ({
      repository,
      value: 1 + Math.random() * 4,
      trend: this.getRandomTrend(),
    }));
  }

  /**
   * Generate mock repository analytics
   */
  private static generateRepositoryAnalytics(repositories?: string[]) {
    const repoNames = repositories || this.getRepositories();
    return repoNames.map(repository => ({
      repository,
      commitFrequency: {
        value: 10 + Math.random() * 20,
        unit: 'per day',
        trend: this.getRandomTrend(),
        previousValue: 15,
        changePercentage: -5 + Math.random() * 15,
      },
      deploymentFrequency: {
        value: 2 + Math.random() * 3,
        unit: 'per day',
        trend: this.getRandomTrend(),
        previousValue: 2.5,
        changePercentage: 5 + Math.random() * 10,
      },
      leadTime: {
        value: 12 + Math.random() * 24,
        unit: 'hours',
        trend: this.getRandomTrend(),
        previousValue: 18,
        changePercentage: -10 + Math.random() * 15,
      },
      changeFailureRate: {
        value: 10 + Math.random() * 15,
        unit: 'percent',
        trend: this.getRandomTrend(),
        previousValue: 12,
        changePercentage: -5 + Math.random() * 10,
      },
    }));
  }

  /**
   * Generate mock trend data
   */
  private static generateTrendData(timeRange: { start: Date; end: Date }) {
    const days = Math.floor(
      (timeRange.end.getTime() - timeRange.start.getTime()) /
        (1000 * 60 * 60 * 24)
    );
    const dataPoints = Math.min(days, 30);

    return Array.from({ length: dataPoints }, (_, i) => {
      const date = new Date(timeRange.start);
      date.setDate(date.getDate() + Math.floor((i * days) / dataPoints));

      return {
        timestamp: date,
        value: 35 + Math.random() * 20 + Math.sin(i / 5) * 10,
        label: `Day ${i + 1}`,
      };
    });
  }

  /**
   * Generate mock burndown data
   */
  private static generateBurndownData() {
    const sprintDays = 10;
    const totalWork = 100;

    return Array.from({ length: sprintDays + 1 }, (_, day) => {
      const ideal = totalWork - (totalWork / sprintDays) * day;
      const actual =
        totalWork - (totalWork / sprintDays) * day + (Math.random() - 0.5) * 15;

      const date = new Date();
      date.setDate(date.getDate() - (sprintDays - day));

      return {
        date,
        remainingWork: Math.max(0, actual),
        idealRemaining: Math.max(0, ideal),
      };
    });
  }

  /**
   * Generate mock blockers
   */
  private static generateBlockers() {
    const blockerTypes = ['Technical', 'Dependency', 'Resource', 'External'];
    const count = Math.floor(Math.random() * 5);

    return Array.from({ length: count }, (_, i) => ({
      id: `BLOCK-${i + 1}`,
      title: `Blocker ${i + 1}`,
      type: blockerTypes[Math.floor(Math.random() * blockerTypes.length)],
      age: Math.floor(Math.random() * 10) + 1,
      impact: Math.random() > 0.5 ? 'high' : 'medium',
    }));
  }

  /**
   * Generate mock recent activity
   */
  private static generateRecentActivity() {
    const activities = [
      'Merged PR #123: Add user authentication',
      'Closed issue #456: Fix login bug',
      'Opened PR #789: Refactor dashboard component',
      'Reviewed PR #234: Update API endpoints',
      'Committed: Improve test coverage',
    ];

    return Array.from({ length: 5 }, (_, i) => ({
      id: `activity-${i}`,
      type: ['commit', 'pr', 'issue', 'review'][Math.floor(Math.random() * 4)],
      description: activities[i % activities.length],
      timestamp: new Date(
        Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000
      ).toISOString(),
    }));
  }

  /**
   * Get random rating for DORA metrics
   */
  private static getRandomRating(): 'elite' | 'high' | 'medium' | 'low' {
    const rand = Math.random();
    if (rand > 0.7) return 'elite';
    if (rand > 0.5) return 'high';
    if (rand > 0.3) return 'medium';
    return 'low';
  }

  /**
   * Generate mock business units list
   */
  static getUnits(): string[] {
    return [
      'Engineering',
      'Product Development',
      'Cloud Services',
      'Data & Analytics',
      'Platform Infrastructure',
    ];
  }

  /**
   * Generate mock unit to programs mapping
   */
  static getUnitProgramMap(): Record<string, string[]> {
    return {
      Engineering: ['Program A', 'Program B'],
      'Product Development': ['Program B', 'Program C'],
      'Cloud Services': ['Program C', 'Digital Transformation'],
      'Data & Analytics': ['Program A', 'Digital Transformation'],
      'Platform Infrastructure': ['Program A', 'Program C'],
    };
  }

  /**
   * Generate mock teams list
   */
  static getTeams(): string[] {
    return [
      'Alpha Team',
      'Beta Team',
      'Gamma Team',
      'Delta Team',
      'Epsilon Team',
    ];
  }

  /**
   * Generate mock programs list
   */
  static getPrograms(): string[] {
    return ['Program A', 'Program B', 'Program C', 'Digital Transformation'];
  }

  /**
   * Generate mock repositories list
   */
  static getRepositories(): string[] {
    return [
      'frontend-app',
      'backend-api',
      'mobile-app',
      'shared-components',
      'infrastructure',
    ];
  }

  /**
   * Get random trend indicator
   */
  private static getRandomTrend(): TrendIndicator {
    const rand = Math.random();
    return rand > 0.6 ? 'up' : rand > 0.3 ? 'stable' : 'down';
  }
}
